import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { INTEGRATIONS_API_BASE } from 'application/config/apiConfig';
import { API, graphqlOperation } from 'aws-amplify';
import type { GraphQLResult } from '@aws-amplify/api-graphql';
import {
  listStages,
  listStageCategorys,
} from '../../../../../../graphql/queries';
import { ConfirmationModal } from './ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { Add as AddIcon } from '@mui/icons-material';
import { getBearerToken } from '../../../../../common/utils/authUtils';
import { v4 as uuidv4 } from 'uuid';

export const JobNimbusSetup: React.FC = () => {
  const [apiKey, setApiKey] = React.useState('');
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<string>('');
  const [availableWorkflows, setAvailableWorkflows] = React.useState<any[]>([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const [jobnimbusStages, setJobnimbusStages] = React.useState<
    Array<{
      id: string;
      name: string;
      description?: string;
      order?: number;
      numericId?: number;
    }>
  >([]);
  const [paivStages, setPaivStages] = React.useState<
    Array<{
      id: string;
      title: string;
      description?: string;
      sequence?: number;
    }>
  >([]);
  const [mappings, setMappings] = React.useState<
    Array<{
      jobnimbusStage: string;
      jobnimbusStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(true);
  const [contactsCategoryID, setContactsCategoryID] = React.useState<
    string | null
  >(null);
  const [webhookUrl, setWebhookUrl] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [existingIntegrationId, setExistingIntegrationId] = React.useState<
    string | null
  >(null);
  const [isActive, setIsActive] = React.useState<boolean | null>(null);
  const [isDisableOpen, setIsDisableOpen] = React.useState(false);
  const [isToggling, setIsToggling] = React.useState(false);
  const [loadedApiKey, setLoadedApiKey] = React.useState<string>('');
  const [loadedSelectedWorkflow, setLoadedSelectedWorkflow] =
    React.useState<string>('');
  const [loadedSelectedWorkflowName, setLoadedSelectedWorkflowName] =
    React.useState<string>('');
  const [loadedAvailableWorkflows, setLoadedAvailableWorkflows] =
    React.useState<any[]>([]);
  const [loadedJobnimbusStages, setLoadedJobnimbusStages] = React.useState<
    Array<{
      id: string;
      name: string;
      description?: string;
      order?: number;
      numericId?: number;
    }>
  >([]);
  const [loadedMappings, setLoadedMappings] = React.useState<
    Array<{
      jobnimbusStage: string;
      jobnimbusStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  >([]);
  const [webhookDialogOpen, setWebhookDialogOpen] = React.useState(false);
  const navigate = useNavigate();
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);

  // Config options state
  const [defaultRecordType, setDefaultRecordType] =
    React.useState<string>('job'); // 'job' or 'contact'
  const [loadedDefaultRecordType, setLoadedDefaultRecordType] =
    React.useState<string>('job');
  const [jobNameFormat, setJobNameFormat] = React.useState<string>('name_only'); // 'name_only', 'name_and_date', 'name_and_address', 'name_address_date'
  const [loadedJobNameFormat, setLoadedJobNameFormat] =
    React.useState<string>('name_only');
  const [defaultStatus, setDefaultStatus] = React.useState<string>('');
  const [loadedDefaultStatus, setLoadedDefaultStatus] =
    React.useState<string>('');

  // Field mappings state
  const [availableFlexFields, setAvailableFlexFields] = React.useState<
    Array<{ name: string; label: string }>
  >([]);
  const [jobnimbusCustomFields, setJobnimbusCustomFields] = React.useState<
    Array<{ name: string; label: string }>
  >([]);
  const [fieldMappings, setFieldMappings] = React.useState<
    Record<string, string>
  >({}); // Paiv field name -> JobNimbus field name
  const [loadedFieldMappings, setLoadedFieldMappings] = React.useState<
    Record<string, string>
  >({});
  const [enabledFieldMappings, setEnabledFieldMappings] = React.useState<
    Array<string>
  >([]); // Array of Paiv field names that are enabled for mapping
  const [loadedEnabledFieldMappings, setLoadedEnabledFieldMappings] =
    React.useState<Array<string>>([]);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = React.useState(false);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);

  const listIntegrationConfigsQuery = /* GraphQL */ `
    query ListIntegrationConfigs(
      $filter: ModelIntegrationConfigFilterInput
      $limit: Int
      $nextToken: String
    ) {
      listIntegrationConfigs(
        filter: $filter
        limit: $limit
        nextToken: $nextToken
      ) {
        items {
          id
          orgID
          crmName
          isActive
          isDeleted
          apiKey
          categoryID
          stageMapping
          configOptions
          fieldMapping
          createdAt
        }
        nextToken
      }
    }
  `;

  // Helper function to sort mappings by Paiv stage sequence (for loading saved data)
  const sortMappingsByPaivSequence = (
    mappingsToSort: Array<{
      jobnimbusStage: string;
      jobnimbusStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  ) => {
    return [...mappingsToSort].sort((a, b) => {
      // If no Paiv stage is selected, put it at the end
      if (!a.paivStageId && !b.paivStageId) {
        return 0; // Maintain order for unmapped stages
      }
      if (!a.paivStageId) return 1;
      if (!b.paivStageId) return -1;

      // Find the Paiv stages and get their sequences
      const stageA = paivStages.find((s) => s.id === a.paivStageId);
      const stageB = paivStages.find((s) => s.id === b.paivStageId);

      const seqA = stageA?.sequence ?? Number.MAX_SAFE_INTEGER;
      const seqB = stageB?.sequence ?? Number.MAX_SAFE_INTEGER;

      // Sort by sequence first
      if (seqA !== seqB) return seqA - seqB;

      // If sequences are equal, sort by stage title
      const titleA = stageA?.title || '';
      const titleB = stageB?.title || '';
      if (titleA !== titleB) return titleA.localeCompare(titleB);

      // Finally, maintain original order
      return 0;
    });
  };

  // Helper function to sort mappings by JobNimbus stage order/id (for active mapping)
  const sortMappingsByJobNimbusOrder = (
    mappingsToSort: Array<{
      jobnimbusStage: string;
      jobnimbusStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  ) => {
    return [...mappingsToSort].sort((a, b) => {
      // Always sort by JobNimbus stage order/id first, regardless of Paiv stage selection
      const jobnimbusStageA = jobnimbusStages.find(
        (s) => s.id === a.jobnimbusStageId || s.name === a.jobnimbusStage
      );
      const jobnimbusStageB = jobnimbusStages.find(
        (s) => s.id === b.jobnimbusStageId || s.name === b.jobnimbusStage
      );

      // Get order or numeric id for sorting
      const orderA = (jobnimbusStageA as any)?.order;
      const orderB = (jobnimbusStageB as any)?.order;
      const idA = (jobnimbusStageA as any)?.numericId;
      const idB = (jobnimbusStageB as any)?.numericId;

      // Sort by order first
      if (orderA !== undefined && orderB !== undefined) {
        return orderA - orderB;
      }
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;

      // Then by numeric id
      if (idA !== undefined && idB !== undefined) {
        return idA - idB;
      }
      if (idA !== undefined) return -1;
      if (idB !== undefined) return 1;

      // Fallback to maintaining original order (index-based)
      return 0;
    });
  };

  // Load Contacts category and Paiv stages
  useEffect(() => {
    (async () => {
      try {
        const orgID = (window as any)?.me?.orgID;
        if (!orgID) {
          return;
        }
        try {
          // Handle pagination to get all categories
          let nextToken: string | null = null;
          let allCats: any[] = [];

          do {
            const catsRes = (await API.graphql(
              graphqlOperation(listStageCategorys as any, {
                filter: { orgID: { eq: orgID }, isDeleted: { eq: false } },
                limit: 1000,
                nextToken,
              })
            )) as any;

            if (catsRes?.errors) {
              console.error('[JobNimbusSetup] GraphQL errors:', catsRes.errors);
            }

            const pageItems = (
              catsRes?.data?.listStageCategorys?.items || []
            ).filter(Boolean);
            allCats = allCats.concat(pageItems);
            nextToken = catsRes?.data?.listStageCategorys?.nextToken || null;
          } while (nextToken);

          // Only use the category with exact title "Contacts" (case-insensitive)
          const contacts =
            allCats.find((c: any) => {
              const title = String(c?.title || '').trim();
              return title.toLowerCase() === 'contacts';
            }) || null;

          setContactsCategoryID(contacts?.id || null);

          // Parse flexFields from Contacts category for field mappings
          if (contacts?.flexAttributes) {
            try {
              const flexAttrs =
                typeof contacts.flexAttributes === 'string'
                  ? JSON.parse(contacts.flexAttributes)
                  : contacts.flexAttributes;

              const config = flexAttrs?.config;
              const dealConfig = config?.deal;
              const dealKeys = dealConfig ? Object.keys(dealConfig) : [];

              // The dealConfig structure has displayFields and flexFields directly, not nested under deal types
              // If there's a "Customer" or "Contact" key, use that; otherwise use the top-level fields
              const customerDeal =
                dealConfig?.Customer ||
                dealConfig?.customer ||
                dealConfig?.Contact ||
                dealConfig?.contact ||
                (dealKeys.find((key) => key.toLowerCase().includes('customer'))
                  ? dealConfig[
                      dealKeys.find((key) =>
                        key.toLowerCase().includes('customer')
                      )!
                    ]
                  : null) ||
                (dealKeys.find((key) => key.toLowerCase().includes('contact'))
                  ? dealConfig[
                      dealKeys.find((key) =>
                        key.toLowerCase().includes('contact')
                      )!
                    ]
                  : null) ||
                dealConfig; // Fallback: use dealConfig directly if it has displayFields/flexFields

              // Get displayFields and flexFields - they might be on customerDeal or directly on dealConfig
              const displayFields =
                customerDeal?.displayFields || dealConfig?.displayFields || [];
              const flexFields =
                customerDeal?.flexFields || dealConfig?.flexFields || [];

              if (displayFields.length > 0 || flexFields.length > 0) {
                // Fields to exclude
                const excludedFields = [
                  'contactInformation',
                  'report',
                  'utilityBill',
                  'damageReport',
                  'dateOfLoss',
                  'hailSeverity',
                  'hailCount',
                  'hailSize',
                  'windSeverity',
                  'recordType',
                  'leadSource',
                  'photos',
                  'windSpeed',
                ];

                // Filter fields: must be in displayFields, not excluded, and not WebView/Read Only
                const validFields: Array<{ name: string; label: string }> = [];

                displayFields.forEach((fieldName: string) => {
                  if (excludedFields.includes(fieldName)) {
                    return;
                  }

                  const flexField = flexFields.find(
                    (f: any) => f.name === fieldName
                  );
                  if (!flexField) {
                    return;
                  }

                  // Exclude WebView and Read Only types
                  if (
                    flexField.type === 'WebView' ||
                    flexField.type === 'Read Only'
                  ) {
                    return;
                  }

                  validFields.push({
                    name: fieldName,
                    label: flexField.label || fieldName,
                  });
                });

                setAvailableFlexFields(validFields);
              }
            } catch (err) {
              console.error('[JobNimbusSetup] Error parsing flexFields', err);
            }
          }
        } catch (err) {
          console.error(
            '[JobNimbusSetup] Error fetching/parsing Contacts category:',
            err
          );
        }

        let nextToken: string | null = null;
        let all: Array<{
          id: string;
          title: string;
          description?: string;
          sequence?: number;
        }> = [];
        do {
          const res = (await API.graphql(
            graphqlOperation(listStages as any, {
              filter: {
                orgID: { eq: orgID },
                isDeleted: { eq: false },
                isActive: { eq: true },
              },
              limit: 1000,
              nextToken,
            })
          )) as GraphQLResult<{
            listStages: { items: any[]; nextToken: string | null };
          }>;
          const items = (res.data?.listStages.items || []).map((s: any) => ({
            id: s.id,
            title: s.title,
            description: s.description,
            sequence: s.sequence,
          }));
          all = all.concat(items);
          nextToken = res.data?.listStages.nextToken || null;
        } while (nextToken);
        all.sort((a, b) => {
          const as =
            typeof a.sequence === 'number'
              ? a.sequence
              : Number.MAX_SAFE_INTEGER;
          const bs =
            typeof b.sequence === 'number'
              ? b.sequence
              : Number.MAX_SAFE_INTEGER;
          if (as !== bs) return as - bs;
          return String(a.title).localeCompare(String(b.title));
        });
        setPaivStages(all);
      } catch (err) {
        console.error('[JobNimbusSetup] Error in initial useEffect:', err);
      }
    })();
  }, []);

  // Load existing JobNimbus integration
  useEffect(() => {
    (async () => {
      try {
        const orgID = (window as any)?.me?.orgID;
        if (!orgID) return;
        const res = (await API.graphql(
          graphqlOperation(listIntegrationConfigsQuery as any, {
            filter: {
              orgID: { eq: orgID },
              crmName: { eq: 'jobnimbus' },
              isDeleted: { ne: true },
            },
            limit: 50,
          })
        )) as any;
        const rawItems = (
          res?.data?.listIntegrationConfigs?.items || []
        ).filter(Boolean);

        const items = rawItems.filter(
          (it: any) => String(it?.crmName || '').toLowerCase() === 'jobnimbus'
        );
        const sorted = items.sort((a: any, b: any) =>
          String(b?.createdAt || '').localeCompare(String(a?.createdAt || ''))
        );
        const activeOnly = sorted.filter((it: any) => it?.isActive === true);
        const picked = activeOnly[0] || sorted[0]; // Prefer active, else latest inactive
        if (!picked) {
          // No non-deleted config → keep page blank in edit mode
          setExistingIntegrationId(null);
          setIsActive(null);
          setWebhookUrl(null);
          setApiKey('');
          setLoadedApiKey('');
          setSelectedWorkflow('');
          setLoadedSelectedWorkflow('');
          setAvailableWorkflows([]);
          setJobnimbusStages([]);
          setLoadedJobnimbusStages([]);
          setMappings([]);
          setLoadedMappings([]);
          setDefaultRecordType('job');
          setLoadedDefaultRecordType('job');
          setJobNameFormat('name_only');
          setLoadedJobNameFormat('name_only');
          setDefaultStatus('');
          setLoadedDefaultStatus('');
          setIsEditing(true);
          setIsInitialLoading(false);
          return;
        }
        setExistingIntegrationId(picked?.id || null);
        setIsActive(Boolean(picked?.isActive));
        setApiKey(picked?.apiKey || '');
        setLoadedApiKey(picked?.apiKey || '');

        // Extract workflow from configOptions.defaultRecordType
        let configOptions: any = {};
        try {
          const raw = picked?.configOptions;
          configOptions = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
        } catch {
          configOptions = {};
        }
        // configOptions.defaultRecordType stores the workflow NAME (e.g., "Insurance"), not the ID
        // We'll match it to a workflow ID after workflows are fetched
        const workflowName = configOptions.defaultRecordType || '';
        // Store the workflow name so we can display it even when workflows aren't loaded
        setLoadedSelectedWorkflowName(workflowName);
        // Temporarily set to empty - will be updated after workflows are fetched
        setSelectedWorkflow('');
        setLoadedSelectedWorkflow('');

        // Load other config options
        // mainObjectType is the main object type (job/contact), not the workflow ID
        // Support both 'recordType' (legacy) and 'mainObjectType' (current)
        setDefaultRecordType(
          configOptions.mainObjectType || configOptions.recordType || 'job'
        );
        setLoadedDefaultRecordType(
          configOptions.mainObjectType || configOptions.recordType || 'job'
        );
        setJobNameFormat(configOptions.jobNameFormat || 'name_only');
        setLoadedJobNameFormat(configOptions.jobNameFormat || 'name_only');
        setDefaultStatus(configOptions.defaultStatus || '');
        setLoadedDefaultStatus(configOptions.defaultStatus || '');

        // Load field mappings - will convert labels to field names after custom fields are loaded
        let fieldMapping: Record<string, string> = {};
        try {
          // fieldMapping might be at root level or in config
          const raw = picked?.fieldMapping;
          let parsed: any =
            typeof raw === 'string' ? JSON.parse(raw) : raw || {};

          // Ensure parsed is an object, not a string or other type
          if (typeof parsed === 'string') {
            // If still a string after parsing, try parsing again
            try {
              parsed = JSON.parse(parsed);
            } catch {
              parsed = {};
            }
          }

          // Only use if it's an object with string keys
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            fieldMapping = parsed;
          } else {
            fieldMapping = {};
          }
        } catch (err) {
          console.error('[JobNimbusSetup] Error parsing fieldMapping:', err);
          fieldMapping = {};
        }
        // Store raw mapping - will convert labels to field names once custom fields are loaded
        setFieldMappings(fieldMapping);
        setLoadedFieldMappings(fieldMapping);

        // Extract enabled field mappings (fields that have mappings or were previously enabled)
        const enabledFields = Object.keys(fieldMapping);
        setEnabledFieldMappings(enabledFields);
        setLoadedEnabledFieldMappings(enabledFields);

        // Fetch workflows if we have an API key (to match workflow name to ID)
        if (picked?.apiKey) {
          try {
            const workflowResponse = await fetch(
              `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
                },
                body: JSON.stringify({ apiKey: picked.apiKey.trim() }),
              }
            );
            if (workflowResponse.ok) {
              const workflowData = await workflowResponse.json();
              if (
                workflowData.success &&
                Array.isArray(workflowData.workflows)
              ) {
                setAvailableWorkflows(workflowData.workflows);
                setLoadedAvailableWorkflows(workflowData.workflows); // Store for cancel

                // Match workflow name from configOptions.defaultRecordType to workflow ID
                if (workflowName) {
                  const matchedWorkflow = workflowData.workflows.find(
                    (w: any) => w.name === workflowName
                  );
                  if (matchedWorkflow) {
                    setSelectedWorkflow(matchedWorkflow.id);
                    setLoadedSelectedWorkflow(matchedWorkflow.id);
                    setLoadedSelectedWorkflowName(workflowName); // Store the name
                  }
                }
              }
              // Also extract custom fields if present
              if (workflowData.custom_fields_job) {
                const customFieldsJob = workflowData.custom_fields_job || [];
                const fields = customFieldsJob
                  .map((field: any) => ({
                    name: field.field || field.name || field.field_name || '',
                    label:
                      field.title ||
                      field.label ||
                      field.name ||
                      field.field_name ||
                      '',
                  }))
                  .filter((f: any) => f.name);
                setJobnimbusCustomFields(fields);

                // Convert any label values in fieldMappings to field names
                const currentMappings = fieldMappings || {};
                // Ensure currentMappings is an object
                if (
                  !currentMappings ||
                  typeof currentMappings !== 'object' ||
                  Array.isArray(currentMappings)
                ) {
                  return;
                }

                if (
                  fields.length > 0 &&
                  Object.keys(currentMappings).length > 0
                ) {
                  const convertedMappings: Record<string, string> = {};
                  Object.entries(currentMappings).forEach(
                    ([paivFieldName, storedValue]) => {
                      // Ensure storedValue is a string
                      if (typeof storedValue !== 'string') {
                        return;
                      }
                      const matchingField = fields.find(
                        (f: any) => f.label === storedValue
                      );
                      if (matchingField) {
                        convertedMappings[paivFieldName] = matchingField.name;
                      } else if (
                        fields.find((f: any) => f.name === storedValue)
                      ) {
                        convertedMappings[paivFieldName] = storedValue;
                      }
                    }
                  );
                  if (Object.keys(convertedMappings).length > 0) {
                    setFieldMappings(convertedMappings);
                    setLoadedFieldMappings(convertedMappings);
                  }
                }
              }
            }
          } catch (err) {
            console.error(
              '[JobNimbusSetup] Error fetching workflows on load',
              err
            );
            // Non-fatal error, continue loading
          }
        }

        // Load workflow and stages from existing mapping
        let mappingObj: Record<string, any> = {};
        try {
          const raw = picked?.stageMapping;
          mappingObj = typeof raw === 'string' ? JSON.parse(raw) : raw || {};
        } catch {
          mappingObj = {};
        }

        // Extract stages and mappings
        // stageMapping structure: { "Appointment Scheduled": "paiv-stage-id", "Job Completed": "paiv-stage-id", ... }
        // The mappingObj keys are JobNimbus stage names, values are Paiv stage IDs
        const entries: Array<{
          name: string;
          paivStageId: string;
          position: number;
        }> = Object.keys(mappingObj || {}).map((name, idx) => ({
          name,
          paivStageId: String(mappingObj?.[name] || ''),
          position: idx + 1,
        }));

        // For JobNimbus stages, use the stage names from the mapping as IDs
        const jobnimbusList = entries.map((e) => ({
          id: e.name,
          name: e.name,
        })) as any;
        setJobnimbusStages(jobnimbusList);
        setLoadedJobnimbusStages(jobnimbusList);

        // Map JobNimbus stage names to Paiv stage IDs
        const mapList = entries.map((e) => ({
          jobnimbusStage: e.name,
          jobnimbusStageId: e.name, // Use name as ID since we don't have the actual JobNimbus stage ID
          paivStage:
            paivStages.find((s) => s.id === e.paivStageId)?.title || '',
          paivStageId: e.paivStageId,
        })) as any;

        // Sort by Paiv stage sequence when loading saved data
        const sortedMapList = sortMappingsByPaivSequence(mapList);
        setMappings(sortedMapList);
        setLoadedMappings(sortedMapList);

        const derivedWebhook = `${INTEGRATIONS_API_BASE}/webhooks/jobnimbus/${picked?.id}`;
        setWebhookUrl(derivedWebhook);
        setIsEditing(false);
      } catch (err) {
        console.error('[JobNimbusSetup] Error loading integration:', err);
      } finally {
        setIsInitialLoading(false);
      }
    })();
  }, []);

  // Fetch JobNimbus custom fields when API key exists and convert field mappings
  useEffect(() => {
    (async () => {
      if (!apiKey.trim()) {
        return;
      }

      try {
        // Use the same /stages endpoint (which calls account/settings) but without workflowId
        const response = await fetch(
          `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
            },
            body: JSON.stringify({ apiKey: apiKey.trim() }),
          }
        );

        if (response.ok) {
          const data = await response.json();

          const customFieldsJob = data.custom_fields_job || [];

          // Transform to array with name and label
          // JobNimbus uses 'field' for the field name and 'title' for the label
          const fields = customFieldsJob
            .map((field: any) => ({
              name: field.field || field.name || field.field_name || '',
              label:
                field.title ||
                field.label ||
                field.name ||
                field.field_name ||
                '',
            }))
            .filter((f: any) => f.name); // Filter out any empty names

          setJobnimbusCustomFields(fields);

          // Convert field mappings after custom fields are loaded - use a callback to get current state
          setFieldMappings((currentMappings) => {
            // Ensure currentMappings is an object
            if (
              !currentMappings ||
              typeof currentMappings !== 'object' ||
              Array.isArray(currentMappings)
            ) {
              return {};
            }

            if (fields.length > 0 && Object.keys(currentMappings).length > 0) {
              const convertedMappings: Record<string, string> = {};
              Object.entries(currentMappings).forEach(
                ([paivFieldName, storedValue]) => {
                  // Ensure storedValue is a string
                  if (typeof storedValue !== 'string') {
                    return;
                  }
                  // Check if storedValue is a label (matches any JobNimbus field label)
                  const matchingField = fields.find(
                    (f: { name: string; label: string }) =>
                      f.label === storedValue
                  );
                  if (matchingField) {
                    // Convert label to field name
                    convertedMappings[paivFieldName] = matchingField.name;
                  } else {
                    // Check if it's already a valid field name
                    const isValidFieldName = fields.find(
                      (f: { name: string; label: string }) =>
                        f.name === storedValue
                    );
                    if (isValidFieldName) {
                      // Already a field name, use as-is
                      convertedMappings[paivFieldName] = storedValue;
                    }
                    // Unknown value, clear it (don't add to convertedMappings)
                  }
                }
              );
              if (Object.keys(convertedMappings).length > 0) {
                setLoadedFieldMappings(convertedMappings);
                return convertedMappings;
              }
            }
            return currentMappings;
          });
        } else {
          const errorText = await response.text().catch(() => 'Unknown error');
          console.error(
            '[JobNimbusSetup] Failed to fetch JobNimbus custom fields',
            response.status,
            errorText
          );
        }
      } catch (err) {
        console.error(
          '[JobNimbusSetup] Error fetching JobNimbus custom fields',
          err
        );
        // Silently fail - custom fields are optional
      }
    })();
  }, [apiKey]);

  // Auto-fetch workflows when API key or main object type changes
  useEffect(() => {
    if (!isEditing) return; // Don't auto-fetch when not in edit mode
    if (!apiKey.trim() || !defaultRecordType) return; // Need both to fetch

    // Only fetch if we don't already have workflows for this object type
    // or if the workflows list is empty
    const hasWorkflowsForType = availableWorkflows.some(
      (w: any) =>
        String(w.object_type || '').toLowerCase() ===
        String(defaultRecordType || '').toLowerCase()
    );

    if (availableWorkflows.length === 0 || !hasWorkflowsForType) {
      fetchJobnimbusWorkflows();
    }
  }, [apiKey, defaultRecordType, isEditing]);

  // Auto-fetch stages when workflow is selected
  useEffect(() => {
    if (!isEditing) return; // Don't auto-fetch when not in edit mode
    if (!selectedWorkflow || selectedWorkflow === 'saved-workflow') return; // Need a valid workflow
    if (!apiKey.trim()) return; // Need API key

    // Only fetch if we don't already have stages for this workflow
    if (jobnimbusStages.length === 0 || mappings.length === 0) {
      fetchJobnimbusStages();
    }
  }, [selectedWorkflow, isEditing, apiKey]);

  const fetchJobnimbusWorkflows = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your JobNimbus API key');
      return;
    }

    if (!defaultRecordType) {
      setError('Please select a main object type');
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            apiKey: apiKey.trim(),
            object_type: defaultRecordType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.workflows)) {
        // Filter workflows by object_type
        const filteredWorkflows = data.workflows.filter(
          (w: any) =>
            String(w.object_type || '').toLowerCase() ===
            String(defaultRecordType || '').toLowerCase()
        );
        setAvailableWorkflows(filteredWorkflows);

        // If we have a saved workflow, try to match it to the filtered workflows
        if (loadedSelectedWorkflow && filteredWorkflows.length > 0) {
          const matchedWorkflow = filteredWorkflows.find(
            (w: any) => w.id === loadedSelectedWorkflow
          );
          if (matchedWorkflow) {
            setSelectedWorkflow(loadedSelectedWorkflow);
          } else {
            // Saved workflow not found in filtered list, clear selection
            setSelectedWorkflow('');
          }
        }

        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch JobNimbus workflows');
      }

      // Extract custom fields if present
      if (data.custom_fields_job) {
        const customFieldsJob = data.custom_fields_job || [];
        const fields = customFieldsJob
          .map((field: any) => ({
            name: field.field || field.name || field.field_name || '',
            label:
              field.title ||
              field.label ||
              field.name ||
              field.field_name ||
              '',
          }))
          .filter((f: any) => f.name);
        setJobnimbusCustomFields(fields);
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] fetch workflows error', err);
      setError(err.message || 'Failed to fetch JobNimbus workflows');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchJobnimbusStages = async () => {
    if (!selectedWorkflow) {
      setError('Please select a workflow first');
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            apiKey: apiKey.trim(),
            workflowId: selectedWorkflow,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.stages)) {
        const stages = data.stages.map((stage: any) => ({
          id: stage.id || stage.stageId || String(Math.random()),
          name: stage.name || stage.title || 'Unnamed Stage',
          description: stage.description || '',
          order: typeof stage.order === 'number' ? stage.order : undefined,
          numericId:
            typeof stage.id === 'number'
              ? stage.id
              : typeof stage.id === 'string'
              ? parseInt(stage.id, 10)
              : undefined,
        }));

        // Sort stages by order field first, then by id if order is not available
        const sortedStages = [...stages].sort((a, b) => {
          // If both have order, sort by order
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          // If only one has order, prioritize it
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          // If no order, sort by numeric id
          if (a.numericId !== undefined && b.numericId !== undefined) {
            return a.numericId - b.numericId;
          }
          // If only one has numeric id, prioritize it
          if (a.numericId !== undefined) return -1;
          if (b.numericId !== undefined) return 1;
          // If neither has order or numeric id, maintain original order
          return 0;
        });

        setJobnimbusStages(sortedStages);

        // Initialize mappings with empty Paiv stage selections (using sorted stages)
        const initialMappings = sortedStages.map((stage: any) => ({
          jobnimbusStage: stage.name,
          jobnimbusStageId: stage.id,
          paivStage: '',
          paivStageId: '',
        }));
        // Sort mappings by JobNimbus order when actively mapping
        const sortedMappings = sortMappingsByJobNimbusOrder(initialMappings);
        setMappings(sortedMappings);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch JobNimbus stages');
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] fetch stages error', err);
      setError(err.message || 'Failed to fetch JobNimbus stages');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your JobNimbus API key');
      return;
    }

    if (mappings.length === 0) {
      setError('Please fetch JobNimbus stages first');
      return;
    }

    // Get the actual workflow ID (not the placeholder)
    const actualWorkflowId =
      selectedWorkflow === 'saved-workflow'
        ? loadedSelectedWorkflow
        : selectedWorkflow;
    if (!actualWorkflowId) {
      setError('Please select a workflow first');
      return;
    }

    setIsSaving(true);
    setError(null);

    // Webhook base URL - defined outside try block for use in success handler
    const webhookBaseUrl =
      'https://266s5nz9rc.execute-api.us-west-2.amazonaws.com/prod/webhooks/jobnimbus';

    // Generate UUID for new records, use existing ID for updates
    // Defined outside try block so it's accessible in success handler
    const integrationId = existingIntegrationId || uuidv4();

    try {
      const orgID = (window as any)?.me?.orgID;
      if (!orgID) {
        throw new Error('Organization ID not found');
      }

      // Construct webhookUrl: base URL + integration ID
      const webhookUrlValue = `${webhookBaseUrl}/${integrationId}`;

      // Get current timestamp in ISO format
      const now = new Date().toISOString();

      const payload: any = {
        orgID,
        userId: (window as any)?.me?.id || null,
        locations: [
          {
            apiKey: apiKey.trim(),
            categoryID: contactsCategoryID,
            workflowId: actualWorkflowId,
            stageMappings: mappings.map((m) => ({
              jobnimbusStage: m.jobnimbusStage,
              jobnimbusStageId: m.jobnimbusStageId,
              paivStageId: m.paivStageId,
              position: mappings.indexOf(m) + 1,
            })),
          },
        ],
        configOptions: {
          defaultRecordType:
            availableWorkflows.find((w) => w.id === actualWorkflowId)?.name ||
            loadedSelectedWorkflowName ||
            actualWorkflowId, // Workflow name (string)
          mainObjectType: defaultRecordType || 'job',
          jobNameFormat: jobNameFormat || 'name_only',
          defaultStatus: defaultStatus || '',
        },
        fieldMapping: fieldMappings,
        webhookUrl: webhookUrlValue,
        id: integrationId,
        updatedAt: now, // Always set updatedAt
      };

      // Only set createdAt for new records
      if (!existingIntegrationId) {
        payload.createdAt = now;
      }

      // Use the same token access pattern as other endpoints in this file
      let accessToken = (window as any)?.me?.accessToken;

      if (!accessToken) {
        // Try getBearerToken as fallback and populate window.me.accessToken
        try {
          const bearerToken = await getBearerToken();

          if (bearerToken) {
            // Populate window.me.accessToken for consistency with other endpoints
            if (!(window as any).me) {
              (window as any).me = {};
            }
            (window as any).me.accessToken = bearerToken;
            accessToken = bearerToken;
          } else {
            throw new Error(
              'Authentication token not found. Please refresh the page and try again.'
            );
          }
        } catch (tokenError: any) {
          // If getBearerToken fails, check if it's a network error or auth error
          if (tokenError?.message?.includes('Failed to fetch')) {
            console.error(
              '[JobNimbusSetup] Network error getting token:',
              tokenError
            );
            throw new Error(
              'Network error: Unable to connect to authentication service. Please check your internet connection and try again.'
            );
          }
          console.error('[JobNimbusSetup] getBearerToken failed:', {
            error: tokenError,
            message: tokenError?.message,
            stack: tokenError?.stack,
          });
          throw new Error(
            'Authentication token not found. Please refresh the page and try again.'
          );
        }
      }

      // Build headers object - use same pattern as other endpoints in this file
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      };

      let response;
      try {
        response = await fetch(
          `${INTEGRATIONS_API_BASE}/jobNimbus/setup-jobnimbus-integration`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
          }
        );
      } catch (fetchError: any) {
        console.error('[JobNimbusSetup] Fetch error details:', {
          message: fetchError?.message,
          name: fetchError?.name,
          stack: fetchError?.stack,
          endpoint: `${INTEGRATIONS_API_BASE}/jobNimbus/setup-jobnimbus-integration`,
          fullUrl: `${INTEGRATIONS_API_BASE}/jobNimbus/setup-jobnimbus-integration`,
          hasToken: !!accessToken,
          tokenLength: accessToken?.length || 0,
        });

        // Check if it's a CORS error
        if (fetchError?.message === 'Failed to fetch') {
          throw new Error(`Network/CORS error: Unable to reach the API endpoint. This may be a CORS configuration issue. Please check:
1. The endpoint exists in API Gateway: /jobNimbus/setup-jobnimbus-integration
2. CORS is enabled for this endpoint
3. Your network connection is working`);
        }

        throw new Error(
          `Network error: ${
            fetchError?.message ||
            'Failed to connect to server. Please check your internet connection and try again.'
          }`
        );
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || `HTTP ${response.status}` };
        }
        console.error('[JobNimbusSetup] API error response:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        throw new Error(
          errorData.message ||
            errorData.error ||
            `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result = await response.json();

      // Check for error messages in the response (even if HTTP 200)
      if (result.message && result.message.includes('Invalid key=value pair')) {
        throw new Error(
          result.message ||
            'Authorization error from API. The endpoint may be misconfigured.'
        );
      }

      if (result.success) {
        // Use the ID from the response (or the one we generated)
        const savedId = result.id || integrationId;
        setExistingIntegrationId(savedId);

        // Update webhookUrl with the saved ID
        const savedWebhookUrl = `${webhookBaseUrl}/${savedId}`;
        setWebhookUrl(savedWebhookUrl);

        setSaveMsg('JobNimbus integration saved successfully!');
        setIsEditing(false);
        setIsActive(true);

        // Show webhook URL dialog
        setWebhookDialogOpen(true);

        // Update loaded state for cancel functionality
        setLoadedApiKey(apiKey);
        setLoadedJobnimbusStages([...jobnimbusStages]);
        setLoadedMappings([...mappings]);
        setLoadedDefaultRecordType(defaultRecordType);
        setLoadedJobNameFormat(jobNameFormat);
        setLoadedDefaultStatus(defaultStatus);
        setLoadedFieldMappings({ ...fieldMappings });
        setLoadedEnabledFieldMappings([...enabledFieldMappings]);
      } else {
        throw new Error(
          result.message || 'Failed to save JobNimbus integration'
        );
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] save error', err);
      setError(err.message || 'Failed to save JobNimbus integration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setApiKey(loadedApiKey);
    setSelectedWorkflow(loadedSelectedWorkflow);
    setAvailableWorkflows([...loadedAvailableWorkflows]); // Restore workflows
    setJobnimbusStages([...loadedJobnimbusStages]);
    setMappings([...loadedMappings]);
    setDefaultRecordType(loadedDefaultRecordType);
    setJobNameFormat(loadedJobNameFormat);
    setDefaultStatus(loadedDefaultStatus);
    setFieldMappings({ ...loadedFieldMappings });
    setEnabledFieldMappings([...loadedEnabledFieldMappings]);
    setAddFieldDialogOpen(false);
    setIsEditing(false);
    setError(null);
    setSaveMsg(null);
  };

  const handleEnable = async () => {
    if (!existingIntegrationId) return;

    setIsToggling(true);
    setError(null);
    setSaveMsg(null);
    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/setup-jobnimbus-integration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            id: existingIntegrationId,
            isActive: true,
          }),
        }
      );

      const result = await response.json();
      if (response.ok && result?.success) {
        setIsActive(true);
        setSaveMsg('JobNimbus integration enabled successfully');
      } else {
        throw new Error(result?.error || 'Failed to enable integration');
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] enable error', err);
      setError(err?.message || 'Failed to enable integration');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDisable = async () => {
    if (!existingIntegrationId) return;

    setIsToggling(true);
    setError(null);
    setSaveMsg(null);
    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/setup-jobnimbus-integration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            id: existingIntegrationId,
            isActive: false,
          }),
        }
      );

      const result = await response.json();
      if (response.ok && result?.success) {
        setIsActive(false);
        setSaveMsg('JobNimbus integration disabled successfully');
      } else {
        throw new Error(result?.error || 'Failed to disable integration');
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] disable error', err);
      setError(err?.message || 'Failed to disable integration');
    } finally {
      setIsToggling(false);
      setIsDisableOpen(false);
    }
  };

  const handleRemove = async () => {
    if (!existingIntegrationId) return;

    setIsToggling(true);
    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/setup-jobnimbus-integration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            id: existingIntegrationId,
            isDeleted: true,
          }),
        }
      );

      if (response.ok) {
        // Clear all fields
        setExistingIntegrationId(null);
        setIsActive(null);
        setWebhookUrl(null);
        setApiKey('');
        setLoadedApiKey('');
        setSelectedWorkflow('');
        setLoadedSelectedWorkflow('');
        setAvailableWorkflows([]);
        setJobnimbusStages([]);
        setLoadedJobnimbusStages([]);
        setMappings([]);
        setLoadedMappings([]);
        setDefaultRecordType('job');
        setLoadedDefaultRecordType('job');
        setJobNameFormat('name_only');
        setLoadedJobNameFormat('name_only');
        setDefaultStatus('');
        setLoadedDefaultStatus('');
        setFieldMappings({});
        setLoadedFieldMappings({});
        setEnabledFieldMappings([]);
        setLoadedEnabledFieldMappings([]);
        setIsEditing(true);
        setSaveMsg('JobNimbus integration removed successfully');
      }
    } catch (err) {
      console.error('[JobNimbusSetup] remove error', err);
      setError('Failed to remove integration');
    } finally {
      setIsToggling(false);
      setIsRemoveOpen(false);
    }
  };

  const copyWebhookUrl = async () => {
    if (webhookUrl) {
      try {
        await navigator.clipboard.writeText(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy webhook URL:', err);
      }
    }
  };

  const isConnected = isActive === true;
  const isDisabled = isActive === false;

  // Compute if Add Field Mapping button should be disabled
  const isAddFieldButtonDisabled = React.useMemo(() => {
    return enabledFieldMappings.length >= availableFlexFields.length;
  }, [enabledFieldMappings.length, availableFlexFields.length]);

  // Show loading spinner while initial data is being loaded
  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          mt: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: 10 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          JobNimbus Integration Setup
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isDisabled && !isEditing && (
            <Button
              variant="contained"
              color="success"
              onClick={handleEnable}
              disabled={isToggling}
            >
              {isToggling ? 'Enabling...' : 'Enable Integration'}
            </Button>
          )}
          {isConnected && !isEditing && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setIsDisableOpen(true)}
              disabled={isToggling}
            >
              Disable Integration
            </Button>
          )}
          {isConnected && isEditing && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsRemoveOpen(true)}
              disabled={isToggling}
            >
              Remove Integration
            </Button>
          )}
          {isEditing ? (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Integration'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditing(true);
                // Clear available workflows so user must fetch them again
                // But keep selectedWorkflow and loadedSelectedWorkflowName so it can be displayed (just not selectable)
                setAvailableWorkflows([]);
                // Keep stages and mappings if they exist - don't clear them on edit
                // They will only be cleared if user changes workflow or fetches new stages
                // Note: We keep selectedWorkflow set so renderValue can show the name
                // But MUI needs a matching option, so we'll handle this in the Select component
              }}
            >
              Edit Configuration
            </Button>
          )}
          {isEditing && (
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      {webhookUrl && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              Webhook URL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Where do I add this?
            </Typography>
            <Tooltip title="Learn where to add the webhook URL in JobNimbus">
              <IconButton
                size="small"
                onClick={() =>
                  navigate('/settings/integrations/jobnimbus/help')
                }
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {webhookUrl}
          </Typography>
          <Button
            size="small"
            onClick={copyWebhookUrl}
            sx={{ mt: 1 }}
            variant="outlined"
          >
            {copied ? 'Copied!' : 'Copy Webhook URL'}
          </Button>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {saveMsg && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {saveMsg}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">API Key</Typography>
          <Tooltip title="Get your API key from JobNimbus Settings > API Keys">
            <IconButton
              size="small"
              onClick={() => navigate('/settings/integrations/jobnimbus/help')}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter your JobNimbus API key"
          disabled={!isEditing}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Main Object Type</InputLabel>
          <Select
            value={defaultRecordType}
            onChange={(e) => {
              const newType = e.target.value;
              setDefaultRecordType(newType);
              // Clear workflows when object type changes (will auto-fetch via useEffect)
              setAvailableWorkflows([]);
              setSelectedWorkflow('');
              setJobnimbusStages([]);
              setMappings([]);
            }}
            disabled={!isEditing}
            label="Main Object Type"
            sx={{
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
              },
              '& .MuiSelect-select': {
                backgroundColor: '#ffffff',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: '#ffffff',
                },
              },
            }}
          >
            <MenuItem value="job">Job</MenuItem>
            <MenuItem value="contact">Contact</MenuItem>
          </Select>
        </FormControl>

        {isFetching && availableWorkflows.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Fetching workflows...
            </Typography>
          </Box>
        )}

        {(availableWorkflows.length > 0 || selectedWorkflow) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Select Workflow
            </Typography>
            <FormControl fullWidth>
              <Select
                value={
                  availableWorkflows.length > 0
                    ? selectedWorkflow
                    : loadedSelectedWorkflowName
                    ? 'saved-workflow'
                    : ''
                }
                onChange={(e) => {
                  const newWorkflow = e.target.value;
                  if (newWorkflow === 'saved-workflow') return; // Prevent changing when workflows aren't loaded
                  setSelectedWorkflow(newWorkflow);
                  // Clear stages and mappings when workflow changes (will auto-fetch via useEffect)
                  if (newWorkflow !== selectedWorkflow && newWorkflow !== '') {
                    setJobnimbusStages([]);
                    setMappings([]);
                  }
                }}
                disabled={!isEditing || availableWorkflows.length === 0}
                displayEmpty
                renderValue={(value) => {
                  if (!value || value === '')
                    return <em>Choose a workflow...</em>;
                  if (value === 'saved-workflow') {
                    // Show saved workflow name when workflows aren't loaded
                    return loadedSelectedWorkflowName || 'Saved Workflow';
                  }
                  // If workflows are loaded, show the workflow name
                  if (availableWorkflows.length > 0) {
                    const workflow = availableWorkflows.find(
                      (w) => w.id === value
                    );
                    return workflow ? workflow.name : value;
                  }
                  // Fallback: show the value
                  return value;
                }}
                sx={{
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                  },
                  '& .MuiSelect-select': {
                    backgroundColor: '#ffffff',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Choose a workflow...</em>
                </MenuItem>
                {availableWorkflows.length === 0 &&
                  loadedSelectedWorkflowName && (
                    <MenuItem value="saved-workflow" disabled>
                      {loadedSelectedWorkflowName}
                    </MenuItem>
                  )}
                {availableWorkflows.map((workflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isFetching &&
              jobnimbusStages.length === 0 &&
              selectedWorkflow &&
              selectedWorkflow !== 'saved-workflow' && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Fetching stages...
                  </Typography>
                </Box>
              )}
          </Box>
        )}
      </Box>

      {/* Config Options Section */}
      {selectedWorkflow && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Config Options
          </Typography>
          <Box
            sx={{
              p: 3,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected Workflow
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {availableWorkflows.length > 0
                  ? availableWorkflows.find((w) => w.id === selectedWorkflow)
                      ?.name || selectedWorkflow
                  : loadedSelectedWorkflowName || selectedWorkflow}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Main Object Type
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {defaultRecordType === 'job' ? 'Job' : 'Contact'}
              </Typography>
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>Job Name Format</InputLabel>
              <Select
                value={jobNameFormat}
                onChange={(e) => setJobNameFormat(e.target.value)}
                disabled={!isEditing}
                label="Job Name Format"
                sx={{
                  backgroundColor: '#ffffff',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                  },
                  '& .MuiSelect-select': {
                    backgroundColor: '#ffffff',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#ffffff',
                    },
                  },
                }}
              >
                <MenuItem value="name_only">Name Only</MenuItem>
                <MenuItem value="name_with_date">Name with Date</MenuItem>
                <MenuItem value="name_with_job_number">
                  Name with Job Number
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {jobnimbusStages.length > 0 && paivStages.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Stage Mappings
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...paivStages]
              .sort((a, b) => {
                const seqA = a.sequence ?? Number.MAX_SAFE_INTEGER;
                const seqB = b.sequence ?? Number.MAX_SAFE_INTEGER;
                if (seqA !== seqB) return seqA - seqB;
                return a.title.localeCompare(b.title);
              })
              .map((paivStage) => {
                // Find the mapping for this Paiv stage
                const mapping = mappings.find(
                  (m) => m.paivStageId === paivStage.id
                );
                const mappedJobnimbusStageId =
                  mapping?.jobnimbusStageId || mapping?.jobnimbusStage || '';

                return (
                  <Box
                    key={paivStage.id}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Paiv Stage
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {paivStage.title}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        JobNimbus Stage
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={mappedJobnimbusStageId || ''}
                          onChange={(e) => {
                            const selectedJobnimbusStageId = e.target.value;
                            const selectedJobnimbusStage = jobnimbusStages.find(
                              (s) =>
                                s.id === selectedJobnimbusStageId ||
                                s.name === selectedJobnimbusStageId
                            );

                            // Remove any existing mapping for this Paiv stage
                            let newMappings = mappings.filter(
                              (m) => m.paivStageId !== paivStage.id
                            );

                            // Remove any existing mapping for the selected JobNimbus stage (one-to-one mapping)
                            if (selectedJobnimbusStageId) {
                              newMappings = newMappings.filter(
                                (m) =>
                                  m.jobnimbusStageId !==
                                    selectedJobnimbusStageId &&
                                  m.jobnimbusStage !== selectedJobnimbusStageId
                              );

                              // Add new mapping
                              newMappings.push({
                                jobnimbusStage:
                                  selectedJobnimbusStage?.name ||
                                  selectedJobnimbusStageId,
                                jobnimbusStageId:
                                  selectedJobnimbusStage?.id ||
                                  selectedJobnimbusStageId,
                                paivStage: paivStage.title,
                                paivStageId: paivStage.id,
                              });
                            }

                            // Sort mappings by JobNimbus order after update (when in edit mode)
                            const sortedMappings = isEditing
                              ? sortMappingsByJobNimbusOrder(newMappings)
                              : sortMappingsByPaivSequence(newMappings);
                            setMappings(sortedMappings);
                          }}
                          disabled={!isEditing}
                          displayEmpty
                          sx={{
                            backgroundColor: '#ffffff',
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#ffffff',
                            },
                            '& .MuiSelect-select': {
                              backgroundColor: '#ffffff',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: '#ffffff',
                              },
                            },
                          }}
                          renderValue={(value) => {
                            if (!value) return <em>Select JobNimbus Stage</em>;
                            const stage = jobnimbusStages.find(
                              (s) => s.id === value || s.name === value
                            );
                            return stage?.name || value;
                          }}
                        >
                          <MenuItem value="">
                            <em>Select JobNimbus Stage</em>
                          </MenuItem>
                          {jobnimbusStages
                            .sort((a, b) => {
                              const orderA = (a as any)?.order;
                              const orderB = (b as any)?.order;
                              const idA = (a as any)?.numericId;
                              const idB = (b as any)?.numericId;

                              // Sort by order first
                              if (
                                orderA !== undefined &&
                                orderB !== undefined
                              ) {
                                return orderA - orderB;
                              }
                              if (orderA !== undefined) return -1;
                              if (orderB !== undefined) return 1;

                              // Then by numeric id
                              if (idA !== undefined && idB !== undefined) {
                                return idA - idB;
                              }
                              if (idA !== undefined) return -1;
                              if (idB !== undefined) return 1;

                              // Maintain original order if no order/id available (don't sort alphabetically)
                              return 0;
                            })
                            .map((stage) => (
                              <MenuItem key={stage.id} value={stage.id}>
                                {stage.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>
      )}

      {/* Field Mappings Section */}
      {jobnimbusCustomFields.length > 0 && availableFlexFields.length === 0 && (
        <Box
          sx={{ mb: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}
        >
          <Typography variant="body2" color="info.dark">
            <strong>Note:</strong> JobNimbus custom fields are loaded, but Paiv
            fields could not be loaded. Create a new field mapping in Paiv to
            sync JobNimbus fields to Paiv fields. The Field Mappings section
            will appear once Paiv fields are available.
          </Typography>
        </Box>
      )}
      {/* Field Mappings Section */}
      {jobnimbusCustomFields.length > 0 && availableFlexFields.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Field Mappings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contact information (Name, Email, Address, etc.) and Photos are
                synced by default.
              </Typography>
            </Box>
            {isEditing && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddFieldDialogOpen(true);
                }}
                disabled={isAddFieldButtonDisabled}
              >
                Add Field Mapping
              </Button>
            )}
          </Box>

          {enabledFieldMappings.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No field mappings configured. Click &quot;Add Field
                Mapping&quot; to map a Paiv field to a JobNimbus field.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {enabledFieldMappings.map((fieldName) => {
                const field = availableFlexFields.find(
                  (f) => f.name === fieldName
                );
                if (!field) return null;
                const mappedValue = fieldMappings[fieldName] || '';
                return (
                  <Box
                    key={fieldName}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        Paiv Field
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {field.label}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        JobNimbus Field
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <FormControl fullWidth size="small">
                          <Select
                            value={mappedValue}
                            onChange={(e) => {
                              const newMappings = {
                                ...fieldMappings,
                                [fieldName]: e.target.value,
                              };
                              setFieldMappings(newMappings);
                            }}
                            disabled={!isEditing}
                            displayEmpty
                            sx={{
                              backgroundColor: '#ffffff',
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#ffffff',
                              },
                              '& .MuiSelect-select': {
                                backgroundColor: '#ffffff',
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: '#ffffff',
                                },
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Select JobNimbus Field</em>
                            </MenuItem>
                            {jobnimbusCustomFields.map((jnField) => (
                              <MenuItem key={jnField.name} value={jnField.name}>
                                {jnField.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {isEditing && (
                          <Tooltip title="Remove field mapping">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newMappings = { ...fieldMappings };
                                delete newMappings[fieldName];

                                // Remove the field and also filter out any fields that don't exist in availableFlexFields
                                const availableFieldNames =
                                  availableFlexFields.map((f) => f.name);
                                const newEnabled = enabledFieldMappings
                                  .filter((name) => name !== fieldName) // Remove the clicked field
                                  .filter((name) =>
                                    availableFieldNames.includes(name)
                                  ); // Only keep fields that exist in availableFlexFields

                                // Also clean up fieldMappings to remove any mappings for fields not in availableFlexFields
                                const cleanedMappings: Record<string, string> =
                                  {};
                                newEnabled.forEach((name) => {
                                  if (newMappings[name]) {
                                    cleanedMappings[name] = newMappings[name];
                                  }
                                });

                                // Update state
                                setFieldMappings(cleanedMappings);
                                setEnabledFieldMappings(newEnabled);
                              }}
                              sx={{
                                mt: 0.5,
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'error.dark',
                                },
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Add Field Mapping Dialog */}
      <Dialog
        open={addFieldDialogOpen}
        onClose={() => setAddFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Field Mapping</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a Paiv field to map to a JobNimbus field.
          </Typography>
          <List>
            {availableFlexFields
              .filter((field) => !enabledFieldMappings.includes(field.name))
              .map((field) => (
                <ListItem
                  key={field.name}
                  button
                  onClick={() => {
                    setEnabledFieldMappings([
                      ...enabledFieldMappings,
                      field.name,
                    ]);
                    setAddFieldDialogOpen(false);
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={field.label}
                    secondary={`Field: ${field.name}`}
                  />
                </ListItem>
              ))}
          </List>
          {availableFlexFields.filter(
            (field) => !enabledFieldMappings.includes(field.name)
          ).length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              All available Paiv fields have been added.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFieldDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={isDisableOpen}
        onCancel={() => setIsDisableOpen(false)}
        onConfirm={handleDisable}
        title="Disable JobNimbus Integration"
        message="Are you sure you want to disable this JobNimbus integration? This will stop syncing data between JobNimbus and Paiv. Remember to remove the webhook from your JobNimbus settings."
        confirmText="Disable"
        severity="warning"
      />

      <ConfirmationModal
        open={isRemoveOpen}
        onCancel={() => setIsRemoveOpen(false)}
        onConfirm={handleRemove}
        title="Remove JobNimbus Integration"
        message="Are you sure you want to permanently remove this JobNimbus integration? This action cannot be undone. Remember to remove the webhook from your JobNimbus settings."
        confirmText="Remove"
        severity="error"
      />

      <Dialog
        open={webhookDialogOpen}
        onClose={() => setWebhookDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>JobNimbus Integration Saved Successfully!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your JobNimbus integration has been saved. Please copy the webhook
            URL below and add it to your JobNimbus settings:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              Webhook URL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Where do I add this?
            </Typography>
            <Tooltip title="Learn where to add the webhook URL in JobNimbus">
              <IconButton
                size="small"
                onClick={() =>
                  navigate('/settings/integrations/jobnimbus/help')
                }
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              value={webhookUrl || ''}
              InputProps={{ readOnly: true }}
              size="small"
            />
            <Button onClick={copyWebhookUrl} variant="outlined">
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Add this webhook URL to your JobNimbus account settings to enable
            real-time data synchronization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWebhookDialogOpen(false)}
            variant="contained"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
